import React, { useEffect, useCallback } from 'react';
import { ABOUT_TEXT, CONTACT_INFO, PROJECTS, SKILLS } from '../data/portfolio';

// ── File content renderer ───────────────────────────────────
function getAboutContent() {
  return {
    title: 'about.txt',
    icon: '📄',
    sections: [
      { heading: 'About Me', body: ABOUT_TEXT },
      {
        heading: 'Technologies & Skills',
        body: null,
        list: SKILLS,
      },
    ],
  };
}

function getFileContent(filename) {
  switch (filename) {
    case 'about.txt':
      return getAboutContent();

    case 'skills.txt':
      return getAboutContent();

    case 'contact.txt':
      return {
        title: 'contact.txt',
        icon: '✉️',
        sections: [
          {
            heading: 'Get In Touch',
            body: "Have a project in mind or just want to say hi?\nLet's build something together.",
            links: [
              { label: 'Email', href: `mailto:${CONTACT_INFO.email}`, display: CONTACT_INFO.email },
              { label: 'GitHub', href: `https://${CONTACT_INFO.github}`, display: CONTACT_INFO.github },
              { label: 'LinkedIn', href: `https://${CONTACT_INFO.linkedin}`, display: CONTACT_INFO.linkedin },
            ],
          }
        ],
      };

    default: {
      // Check for project files
      const projectKey = filename.replace('projects/', '').replace('.txt', '');
      const project = PROJECTS[projectKey];
      if (project) {
        return {
          title: `projects/${projectKey}.txt`,
          icon: '📁',
          sections: [
            {
              heading: project.title,
              body: project.description,
              tags: project.tech,
              link: project.link !== '#' ? project.link : null,
            }
          ],
        };
      }
      return null;
    }
  }
}

export default function FileViewer({ file, onClose }) {
  const content = getFileContent(file);

  // Close on Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  if (!content) return null;

  return (
    <div className="fileviewer-overlay" onClick={onClose}>
      <div className="fileviewer" onClick={(e) => e.stopPropagation()}>
        {/* Title bar */}
        <div className="fileviewer-titlebar">
          <div className="fileviewer-titlebar-left">
            <span className="fileviewer-icon">{content.icon}</span>
            <span className="fileviewer-filename">{content.title}</span>
          </div>
          <button className="fileviewer-close" onClick={onClose}>
            <span>✕</span>
          </button>
        </div>

        {/* Content */}
        <div className="fileviewer-body">
          {content.sections.map((section, i) => (
            <div key={i} className="fileviewer-section">
              <h2 className="fileviewer-heading">{section.heading}</h2>
              <div className="fileviewer-divider" />

              {section.body && (
                <p className="fileviewer-text">{section.body}</p>
              )}

              {section.list && (
                <div className="fileviewer-tags">
                  {section.list.map((item, j) => (
                    <span key={j} className="fileviewer-tag">{item}</span>
                  ))}
                </div>
              )}

              {section.tags && (
                <div className="fileviewer-tags" style={{ marginTop: '1.5rem' }}>
                  {section.tags.map((tag, j) => (
                    <span key={j} className="fileviewer-tag">{tag}</span>
                  ))}
                </div>
              )}

              {section.links && (
                <div className="fileviewer-links">
                  {section.links.map((link, j) => (
                    <a
                      key={j}
                      href={link.href}
                      className="fileviewer-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="fileviewer-link-label">{link.label}</span>
                      <span className="fileviewer-link-value">{link.display}</span>
                      <span className="fileviewer-link-arrow">↗</span>
                    </a>
                  ))}
                </div>
              )}

              {section.link && (
                <a
                  href={section.link}
                  className="fileviewer-project-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Project ↗
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="fileviewer-footer">
          <span className="fileviewer-footer-hint">Press ESC or click outside to close</span>
        </div>
      </div>
    </div>
  );
}
